/**
 * Authentication Service
 * Handles all REST API calls to the Customer Authentication API
 * Base URL: store-auth.wuilt.com
 */

export interface VerifyOTPRequest {
  verificationMethod: "EMAIL" | "SMS" | "WHATSAPP";
  receiver: string;
  storeId: string;
  otp: string;
}

export interface VerifyOTPResponse {
  success: boolean;
  message: string;
  requiresSignup: boolean;
  code?: string;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  token?: string;
}

export interface SignupRequest {
  name: string;
  email: string;
  phone: string;
  storeId: string;
  preferredLocale?: string;
}

export interface SignupResponse {
  success: boolean;
  message: string;
  code: string;
  linkedData: string;
  data?: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    emailVerified?: boolean;
    phoneVerified?: boolean;
  };
  token?: string;
}

export interface LoginRequest {
  emailOrPhone: string;
  storeId: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data?: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    emailVerified?: boolean;
    phoneVerified?: boolean;
  };
  token?: string;
}

export interface CheckSessionResponse {
  success: boolean;
  data?: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    emailVerified?: boolean;
    phoneVerified?: boolean;
  };
  token?: string;
}

export interface LogoutResponse {
  success: boolean;
  message: string;
}

export interface EditModernCustomerRequest {
  customerId: string;
  verificationMethod: "EMAIL" | "SMS" | "WHATSAPP";
  receiver: string;
  storeId: string;
  otp: string;
}

export interface EditModernCustomerResponse {
  success: boolean;
  message: string;
  data?: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    emailVerified?: boolean;
    phoneVerified?: boolean;
  };
}

export interface EditModernCustomerNameRequest {
  customerId: string;
  name: string;
  storeId: string;
}

export interface EditModernCustomerNameResponse {
  success: boolean;
  message: string;
  data?: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    emailVerified?: boolean;
    phoneVerified?: boolean;
  };
}

export interface DeleteAccountRequest {
  customerId: string;
  storeId: string;
}

export interface DeleteAccountResponse {
  success: boolean;
  message: string;
}

export interface AuthError {
  code: string;
  message: string;
  linkedData?: string;
}

interface AuthServiceConfig {
  baseUrl: string;
  timeout: number;
  retryAttempts: number;
}

const DEFAULT_CONFIG: AuthServiceConfig = {
  baseUrl:
    process.env.NEXT_PUBLIC_AUTH_SERVICE_BASE_URL ||
    "https://store-auth.wuilt.com",
  timeout: 30000,
  retryAttempts: 1,
};

/**
 * Calculate exponential backoff delay
 */
function getRetryDelay(attempt: number): number {
  return Math.min(1000 * Math.pow(2, attempt), 10000);
}

/**
 * Make a fetch request with retry logic and timeout
 */
async function fetchWithRetry<T>(
  url: string,
  options: RequestInit,
  config: AuthServiceConfig,
  attempt: number = 0
): Promise<T> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout);

    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw {
        code: errorData.code,
        message: errorData.message,
        linkedData: errorData.linkedData,
      } as AuthError;
    }

    return await response.json();
  } catch (error) {
    // If it's already an AuthError, throw it
    if (error) {
      throw error;
    }

    // Network error - retry if attempts remaining
    if (attempt < config.retryAttempts - 1) {
      const delay = getRetryDelay(attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, config, attempt + 1);
    }

    // All retry attempts exhausted, throw the original error
    throw error;
  }
}

/**
 * Verify OTP code
 */
export async function verifyOTP(
  request: VerifyOTPRequest,
  config: Partial<AuthServiceConfig> = {}
): Promise<VerifyOTPResponse> {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  const response = await fetchWithRetry<VerifyOTPResponse>(
    `${finalConfig.baseUrl}/api/auth/verify-otp`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
      credentials: "include", // Include cookies
    },
    finalConfig
  );

  return response;
}

/**
 * Sign up new user
 */
export async function signup(
  request: SignupRequest,
  config: Partial<AuthServiceConfig> = {},
  token: string = ""
): Promise<SignupResponse> {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  const response = await fetchWithRetry<SignupResponse>(
    `${finalConfig.baseUrl}/api/auth/signup`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
      credentials: "include", // Include cookies
    },
    finalConfig
  );

  return response;
}

/**
 * Login existing user
 */
export async function login(
  request: LoginRequest,
  config: Partial<AuthServiceConfig> = {}
): Promise<LoginResponse> {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  const response = await fetchWithRetry<LoginResponse>(
    `${finalConfig.baseUrl}/api/auth/login`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
      credentials: "include", // Include cookies
    },
    finalConfig
  );

  return response;
}

/**
 * Check current session
 */
export async function checkSession(
  storeId: string,
  config: Partial<AuthServiceConfig> = {}
): Promise<CheckSessionResponse> {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  const response = await fetchWithRetry<CheckSessionResponse>(
    `${finalConfig.baseUrl}/api/auth/check`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ storeId }),
      credentials: "include", // Include cookies
    },
    finalConfig
  );

  return response;
}

/**
 * Logout user
 */
export async function centralizedLogout(
  config: Partial<AuthServiceConfig> = {}
): Promise<LogoutResponse> {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  const response = await fetchWithRetry<LogoutResponse>(
    `${finalConfig.baseUrl}/api/auth/logout`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Include cookies
    },
    finalConfig
  );

  // Remove the auth token cookie after successful logout
  if (response.success) {
    const { eraseCookie, CookieEnum } = await import(
      "../../../shared/utils/cookie"
    );
    eraseCookie(CookieEnum.AUTH_TOKEN, { sameSite: "strict" });
    eraseCookie(CookieEnum.CLIENT_CENTRAL_TOKEN, { sameSite: "strict" });
    localStorage.removeItem(CookieEnum.CLIENT_CENTRAL_TOKEN);
  }

  return response;
}

/**
 * Edit modern customer phone or email
 */
export async function editModernCustomer(
  request: EditModernCustomerRequest,
  config: Partial<AuthServiceConfig> = {}
): Promise<EditModernCustomerResponse> {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  const response = await fetchWithRetry<EditModernCustomerResponse>(
    `${finalConfig.baseUrl}/api/auth/edit-modern-customer`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
      credentials: "include", // Include cookies
    },
    finalConfig
  );

  return response;
}

/**
 * Edit modern customer name
 */
export async function editModernCustomerName(
  request: EditModernCustomerNameRequest,
  config: Partial<AuthServiceConfig> = {}
): Promise<EditModernCustomerNameResponse> {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  const response = await fetchWithRetry<EditModernCustomerNameResponse>(
    `${finalConfig.baseUrl}/api/auth/edit-modern-customer-name`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
      credentials: "include", // Include cookies
    },
    finalConfig
  );

  return response;
}

/**
 * Delete customer account
 * Deletes customer account and sets emailVerified and phoneVerified to false
 */
export async function deleteAccount(
  request: DeleteAccountRequest,
  config: Partial<AuthServiceConfig> = {}
): Promise<DeleteAccountResponse> {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  const response = await fetchWithRetry<DeleteAccountResponse>(
    `${finalConfig.baseUrl}/api/auth/delete-account`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
      credentials: "include", // Include cookies
    },
    finalConfig
  );

  return response;
}

/**
 * Export all functions as a service object
 */
export const authService = {
  verifyOTP,
  signup,
  login,
  checkSession,
  centralizedLogout,
  editModernCustomer,
  editModernCustomerName,
  deleteAccount,
};
